/**
 * Factory for a typed sessionStorage-backed store of a single payload value.
 * `read()` validates the parsed value via the supplied `isValid` predicate; the
 * predicate is also exposed on the returned store so non-storage callers (like
 * `usePendingAuthPurchaseResume`) can validate values from other sources against
 * the same shape.
 */

export interface SessionPayloadStore<T> {
  persist: (payload: T) => void;
  read: () => T | undefined;
  clear: () => void;
  /** Type predicate for the payload — exposed so non-storage callers can validate
   *  values from other sources (e.g. a Hookstate fallback) against the same shape. */
  isValid: (value: unknown) => value is T;
}

export function createSessionPayloadStore<T>(
  storageKey: string,
  isValid: (value: unknown) => value is T,
): SessionPayloadStore<T> {
  return {
    persist(payload: T): void {
      if (typeof window === "undefined") return;
      window.sessionStorage.setItem(storageKey, JSON.stringify(payload));
    },

    read(): T | undefined {
      if (typeof window === "undefined") return undefined;
      const serialized = window.sessionStorage.getItem(storageKey);
      if (!serialized) return undefined;
      try {
        const parsed: unknown = JSON.parse(serialized);
        return isValid(parsed) ? parsed : undefined;
      } catch {
        return undefined;
      }
    },

    clear(): void {
      if (typeof window === "undefined") return;
      window.sessionStorage.removeItem(storageKey);
    },

    isValid,
  };
}
